"use client"

import { useMemo } from "react"
import { Layers, MessageSquare, UserPlus, Users } from "lucide-react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { computeDelta } from "@/lib/analytics/deltas"
import type { SentimentBucket } from "@/lib/analytics/sentiment"
import type { ConversationVolumeResult } from "@/server-actions/conversation-quality"

import {
  channelLabel,
  EmptyNote,
  SectionCard,
  SegmentLegend,
  sessionCount,
  StackedBar,
  type Segment,
} from "./section-parts"

// The `--chart-N` tokens hold bare HSL channels, so they only become a colour once wrapped.
const CHANNEL_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
]

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

/** Best first, as a reader expects — the vocabulary itself runs the other way. */
const SENTIMENT_SEGMENTS: { bucket: SentimentBucket; label: string; color: string }[] = [
  { bucket: "positive", label: "Positive", color: "hsl(var(--sentiment-positive))" },
  { bucket: "neutral", label: "Conditionally positive", color: "hsl(var(--sentiment-conditional))" },
  { bucket: "negative", label: "Negative", color: "hsl(var(--sentiment-negative))" },
]

/** `2026-09-08` → «Sep 8»; the day is already the viewer's local calendar day. */
function shortDay(day: string): string {
  return new Date(`${day}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric" })
}

export function VolumeKpis({ data }: { data: ConversationVolumeResult | undefined }) {
  if (!data) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const cards = [
    { label: "Conversations", icon: Users, value: data.counts.conversations, prev: data.prevCounts.conversations },
    { label: "Sessions", icon: Layers, value: data.counts.sessions, prev: data.prevCounts.sessions },
    { label: "Messages", icon: MessageSquare, value: data.counts.messages, prev: data.prevCounts.messages },
    { label: "New customers", icon: UserPlus, value: data.counts.newCustomers, prev: data.prevCounts.newCustomers },
  ]

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {cards.map((card) => (
        <KpiCard
          key={card.label}
          label={card.label}
          icon={card.icon}
          formattedValue={card.value.toLocaleString()}
          delta={data.hasComparison ? computeDelta(card.value, card.prev) : null}
          // From nothing the badge already reads «new».
          baseline={data.hasComparison && card.prev > 0 ? `from ${card.prev.toLocaleString()}` : undefined}
        />
      ))}
    </div>
  )
}

/** Sessions per day, stacked by channel — «how much came in, and through what». */
export function SessionsByDay({ data }: { data: ConversationVolumeResult | undefined }) {
  const { rows, channels } = useMemo(() => {
    if (!data) return { rows: [], channels: [] as string[] }

    const channels = [...new Set(data.byDay.map((row) => row.channel))]
    const byDay = new Map<string, Record<string, string | number>>()
    for (const row of data.byDay) {
      const day = byDay.get(row.day) ?? { day: row.day }
      day[row.channel] = row.sessions
      byDay.set(row.day, day)
    }
    return { rows: [...byDay.values()], channels }
  }, [data])

  const config = useMemo(
    () =>
      Object.fromEntries(
        channels.map((channel, index) => [
          channel,
          { label: channelLabel(channel), color: CHANNEL_COLORS[index % CHANNEL_COLORS.length] },
        ])
      ),
    [channels]
  )

  return (
    <SectionCard title="Sessions by day" subtitle="stacked by channel">
      {!data ? (
        <Skeleton className="h-[220px] w-full sm:h-[300px]" />
      ) : rows.length === 0 ? (
        <EmptyNote />
      ) : (
        <ChartContainer config={config} className="h-[220px] w-full sm:h-[300px]">
          <BarChart data={rows}>
            <CartesianGrid vertical={false} />
            {/* A phone fits about seven labels; more turn into a grey smear. */}
            <XAxis dataKey="day" tickFormatter={shortDay} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis width={32} tickLine={false} axisLine={false} allowDecimals={false} />
            <ChartTooltip content={<ChartTooltipContent labelFormatter={shortDay} />} />
            <ChartLegend content={(props) => <ChartLegendContent payload={props.payload} verticalAlign={props.verticalAlign} />} />
            {channels.map((channel) => (
              <Bar key={channel} dataKey={channel} stackId="sessions" fill={`var(--color-${channel})`} />
            ))}
          </BarChart>
        </ChartContainer>
      )}
    </SectionCard>
  )
}

export function ChannelBreakdown({ data }: { data: ConversationVolumeResult | undefined }) {
  const segments: Segment[] =
    data?.byChannel.map((row, index) => ({
      key: row.channel,
      label: channelLabel(row.channel),
      count: row.sessions,
      color: CHANNEL_COLORS[index % CHANNEL_COLORS.length],
    })) ?? []
  const total = segments.reduce((sum, segment) => sum + segment.count, 0)

  return (
    <SectionCard title="Channels" subtitle={data && sessionCount(total)}>
      {!data ? (
        <Skeleton className="h-24 w-full" />
      ) : total === 0 ? (
        <EmptyNote />
      ) : (
        <div className="space-y-4">
          <StackedBar segments={segments} total={total} />
          <SegmentLegend segments={segments} total={total} />
        </div>
      )}
    </SectionCard>
  )
}

export function SentimentBreakdown({ data }: { data: ConversationVolumeResult | undefined }) {
  const segments: Segment[] = SENTIMENT_SEGMENTS.map(({ bucket, label, color }) => ({
    key: bucket,
    label,
    color,
    count: data?.sentiment.find((row) => row.bucket === bucket)?.sessions ?? 0,
  }))
  const total = segments.reduce((sum, segment) => sum + segment.count, 0)

  return (
    <SectionCard
      title="Sentiment"
      subtitle={data && `how the customer felt by the end · ${sessionCount(total)} judged`}
    >
      {!data ? (
        <Skeleton className="h-24 w-full" />
      ) : total === 0 ? (
        <EmptyNote />
      ) : (
        <div className="space-y-4">
          <StackedBar segments={segments} total={total} />
          <SegmentLegend segments={segments} total={total} />
        </div>
      )}
    </SectionCard>
  )
}

/**
 * When customers write. The full grid needs room, so below `md` the top hours carry the
 * same answer in a form that fits.
 */
export function BusiestHours({ data }: { data: ConversationVolumeResult | undefined }) {
  const { grid, peak, top } = useMemo(() => {
    const grid = new Map<string, number>()
    let peak = 0
    for (const row of data?.busiestHours ?? []) {
      grid.set(`${row.weekday}-${row.hour}`, row.inbound)
      peak = Math.max(peak, row.inbound)
    }
    const top = [...(data?.busiestHours ?? [])]
      .sort((a, b) => b.inbound - a.inbound)
      .slice(0, 5)
    return { grid, peak, top }
  }, [data])

  return (
    <SectionCard title="Busiest hours" subtitle="inbound messages by weekday and hour">
      {!data ? (
        <Skeleton className="h-40 w-full" />
      ) : data.busiestHours.length === 0 ? (
        <EmptyNote />
      ) : (
        <>
          <div className="hidden space-y-1 md:block">
            {WEEKDAY_LABELS.map((label, weekday) => (
              <div key={label} className="flex items-center gap-1">
                <span className="w-9 shrink-0 text-xs text-muted-foreground">{label}</span>
                {Array.from({ length: 24 }, (_, hour) => {
                  const value = grid.get(`${weekday}-${hour}`) ?? 0
                  return (
                    <span
                      key={hour}
                      title={`${label} ${String(hour).padStart(2, "0")}:00 — ${value}`}
                      className="h-4 flex-1 rounded-sm bg-primary"
                      style={{ opacity: peak > 0 ? 0.1 + (value / peak) * 0.9 : 0.1 }}
                    />
                  )
                })}
              </div>
            ))}
            <div className="flex gap-1 pt-1">
              <span className="w-9 shrink-0" />
              {Array.from({ length: 8 }, (_, i) => (
                <span key={i} className="flex-1 text-center text-xs text-muted-foreground">
                  {String(i * 3).padStart(2, "0")}
                </span>
              ))}
            </div>
          </div>
          <ul className="space-y-2 text-sm md:hidden">
            {top.map((row) => (
              <li key={`${row.weekday}-${row.hour}`} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span>
                    {WEEKDAY_LABELS[row.weekday]} {String(row.hour).padStart(2, "0")}:00
                  </span>
                  <span className="font-medium tabular-nums">{row.inbound}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${peak > 0 ? (row.inbound / peak) * 100 : 0}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </SectionCard>
  )
}

/** Worded as customers coming back, which is as often a new order as an unsolved problem. */
export function ReturningCustomers({ data }: { data: ConversationVolumeResult | undefined }) {
  const buckets = [
    { key: "1", label: "one session" },
    { key: "2-3", label: "came back in 2–3 sessions" },
    { key: "4+", label: "4 or more" },
  ] as const

  return (
    <SectionCard title="Returning customers" subtitle="conversations by how many sessions they had">
      <div className="flex items-start gap-8">
        {!data ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          buckets.map((bucket) => (
            <div key={bucket.key} className="space-y-1">
              <div className="text-2xl font-semibold tabular-nums">
                {data.sessionsPerConversation.find((row) => row.bucket === bucket.key)?.conversations ?? 0}
              </div>
              <div className="text-xs text-muted-foreground">{bucket.label}</div>
            </div>
          ))
        )}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        A new session starts when a customer writes again after the agent&apos;s session timeout — a
        follow-up question, a new order, a thank-you.
      </p>
    </SectionCard>
  )
}
